import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../../../../constants/common'
import {
  NOTIFICATION_FILE_UPLOAD,
  NOTIFICATION_FILE_UPLOADED_WITH_ERRORS,
} from '../../../../../../constants/notification'
import {
  useInlineNotifications,
  useNotification,
  useUserData,
} from '../../../../../../hooks'
import {
  getFormNotificationFilesIsUploading,
  getFormNotificationFilesWithErrors,
} from '../../../../../../utils/notification'
import { useFileUpload } from '../../../../../File'
import {
  getMockViewingDocumentUploads,
  postMockViewingDocuments,
} from '../../../../../RentalPortalViewing'

export function useViewingDocumentUploadForm({
  viewingId,
  onClose,
  onUploaded,
}) {
  const { t } = useTranslation()

  const currentUser = useUserData()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const [files, setFiles] = useState(EMPTY_LIST)
  const [isSaving, setSaving] = useState(false)

  function handleForgetFile() {
    return null
  }

  const {
    filesError,
    filesUploading,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onDeleteFile: handleForgetFile,
    onSetFiles: setFiles,
  })

  function handleGetFiles({ files: pickedFiles }) {
    return getMockViewingDocumentUploads({ files: pickedFiles })
  }

  function handleSave() {
    if (filesUploading.length > 0) {
      handleAddNotification(getFormNotificationFilesIsUploading(t))

      return
    }

    if (filesError.length > 0) {
      handleAddNotification(getFormNotificationFilesWithErrors(t))

      return
    }

    setSaving(true)

    postMockViewingDocuments({ currentUser, files, viewing_id: viewingId })
      .then(() => {
        handleAddSuccessNotification({
          title: t('RentalPortal.ViewingDocumentUploadedNotificationKey', {
            count: files.length,
          }),
        })

        onUploaded()
        onClose()
      })
      .catch(() =>
        handleAddErrorNotification(
          'RentalPortal.ViewingDocumentUploadErrorNotificationKey'
        )
      )
      .finally(() => setSaving(false))
  }

  useEffect(() => {
    if (filesUploading.length === 0) {
      handleRemoveNotification(NOTIFICATION_FILE_UPLOAD)
    }

    if (filesError.length === 0) {
      handleRemoveNotification(NOTIFICATION_FILE_UPLOADED_WITH_ERRORS)
    }
  }, [filesError.length, filesUploading.length])

  return {
    files,
    filesError,
    filesUploading,
    handleGetFiles,
    handleRemoveFile,
    handleSave,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
    isSaving,
    notifications,
  }
}
